import axios from '@/axiosInterceptor';
import dayjs from 'dayjs';

const state = () => ({
  carrierVehicles: [],
  hasCarrierVehiclesLoaded: false,
  showCarrier: null,
  isShowCarrierLoading: false,
  showCarrierSafety: null,
  showCarrierCrashes: null,
  showCarrierViolations: null,
  showCarrierInspections: null,
  showCarrierInsights: [],
  showCarrierInsurancePolicies: [],
  showCarrierHistoricalTouches: [],
  showCarrierELDTouches: [],
  showAssociatedCarriers: {
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
    },
    counters: {
      totalEmails: 0,
      totalPhones: 0,
      totalAddresses: 0,
      totalContacts: 0,
      totalRemoteAddresses: 0,
      totalVehicleVins: 0,
    },
    carriers: [],
    totalAssociated: 0,
  },
  showCarrierEquipment: {
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
    },
    rows: [],
  },
  showCarrierScheduledVehicleCount: null,
  showCarrierNonScheduledVehicleCount: null,
  carrierAgreements: [],
  sortedCarrierAgreements: [],
  searchResults: [],
  myCarrierResults: [],
  myCarrierTotalResultCount: null,
  filters: {
    onlyMyCarrier: false,
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  rulesSyntax: [],
  rulesSyntaxByClassificationId: {},
  loadAnalysis: [],
  rates: [],
  rates_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  viewed_rates: [],
  viewed_rates_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  delivered_rates: [],
  delivered_rates_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  rate_con_emails: [],
  rate_con_emails_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  current_rate: null,
  current_rate_terms: null,
  onlyMyRateCons: false,
  rateConSearchTerm: null,
  powerUnitStats: null,
  associatedCarrierIdPersisted: null,
  failing_eld_statuses: [
    'no_vins_on_coi',
    'eld_vins_not_on_coi',
    'missing_vins_on_eld',
    'some_eld_vin_matches_on_coi',
    'no_eld_vin_matches_on_coi',
  ],
  eld_status: null,
  rate_confirmation: null,
  carrierAreasGeoJson: null,
  eldTouchesGeoJson: null,
  hasHeatmapData: true,
  dashboardConfigs: null,
});

// getters
const getters = {};

// actions
const actions = {
  async requestCargoPolicy(_, policyId) {
    try {
      let res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/insurance_policies/request_cargo_policy?insurance_policy_id=${policyId}`
      );
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e;
      } else {
        throw e;
      }
    }
  },
  async getCarriersDetail({ commit }, id) {
    commit('setShowCarrierLoading', true);

    try {
      let res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/carriers/' +
          id +
          '/detail_for_highway_broker'
      );
      commit('setShowCarrier', res.data);
      console.log('handling carriers#detail response');
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setShowCarrierLoading', false);
    }
  },
  async getCarriersInspections({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_MONITOR + '/api/v1/carriers/' + id + '/inspections'
      );
      commit('setInspections', res.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getAssociatedCarriers({ commit }, { carrierId, page, queries }) {
    const params = {
      page,
    };
    if (queries?.length > 0) {
      params['required_associations'] = queries;
    }
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${carrierId}/associated_carriers`,
        { params }
      );
      commit('setAssociates', res.data);
      commit('setAssociatedPagination', res?.headers);
      commit('setAssociatedCounters', res?.headers);
      commit('setAssociatedCarrierIdPersisted', carrierId);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCarriersCrashes({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_MONITOR + '/api/v1/carriers/' + id + '/crashes'
      );
      commit('setCrashes', res.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  async getCarriersViolations({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_MONITOR + '/api/v1/carriers/' + id + '/violations'
      );
      commit('setViolations', res?.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  async getCarriersSafety({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_MONITOR + '/api/v1/carriers/' + id + '/safety'
      );
      commit('setSafety', res?.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async blockAssociatedCarriers(
    _,
    { carrierId, isTypes, connectionNotes: connection_notes = '' }
  ) {
    const URLS = isTypes.map((isType) =>
      axios.patch(
        process.env.API_MONITOR +
          `/api/v1/carriers/${carrierId}/block_associated_carriers`,
        { is_type: isType, connection_notes }
      )
    );

    try {
      const response = await Promise.all(URLS);
      return response;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async blockAssociatedCarriersById(
    _,
    { carrierId, carrierIds, is_type, connectionNotes: connection_notes = '' }
  ) {
    const body = {
      carrier_ids: carrierIds,
      is_type,
      connection_notes,
    };
    try {
      const response = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/carriers/${carrierId}/block_associated_carriers_by_ids`,
        body
      );
      return response;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCarriersInsurancePolicies({ commit }, id) {
    const params = {
      carrier_id: id,
    };
    try {
      const res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/insurance_policies/active_by_carrier_id?with_cancelled=true&with_active_pending=true',
        { params }
      );
      commit('setShowCarrierInsurancePolicies', res?.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCarrierAgreements({ commit }, carrierId) {
    try {
      let res = await axios.get(
        process.env.API_IDENTITY +
          '/api/v1/onboarding_packet_contracts/search_by_carrier_id?carrier_id=' +
          carrierId
      );
      commit('setCarrierAgreements', res?.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getRateConfirmationEndpointsByCarrierId({ commit }, carrierId) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${carrierId}/rate_confirmation_endpoints`
      );
      commit('setRateConfirmation', res.data);
      return res?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getCarrierInsights({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_MONITOR + '/api/v1/carriers/' + id + '/insights'
      );
      commit('setShowCarrierInsights', res.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCarrierVehicles({ commit }, id) {
    commit('setHasCarrierVehiclesLoaded', false);
    try {
      let res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/carriers/' +
          id +
          '/eld_carrier_vehicles'
      );
      commit('setHasCarrierVehiclesLoaded', true);
      if (res.status < 400 && res?.data) {
        commit('setCarrierVehicles', res.data.eld_carrier_vehicles || []);
        return res;
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
    } catch (e) {
      commit('setHasCarrierVehiclesLoaded', true);
      commit('setCarrierVehicles', []);
      console.log('error', e);
      return e;
    }
  },
  async getCarrierAgreementDetails({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_IDENTITY + '/api/v1/onboarding_packet_contracts/' + id
      );
      commit('setCarrierAgreements', res.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCarriersHistoricaltouches({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/carriers/' +
          id +
          '/historical_touches_by_five_zip_areas'
      );
      commit('setShowCarrierHistoricalTouches', res.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCarriersELDTouches({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/carriers/' +
          id +
          '/recently_visited_areas_verified_by_eld'
      );
      // testing only
      // const res = await axios.get(`http://localhost:4000/eld_touches`);
      if (res?.data) {
        commit('setShowCarrierELDTouches', res.data);

        const geoJsonFeatures = [];

        if (res.data.vehicles && Array.isArray(res.data.vehicles)) {
          res.data.vehicles.forEach((vehicle) => {
            if (vehicle.areas && Array.isArray(vehicle.areas)) {
              vehicle.areas.forEach((area) => {
                if (area.lat && area.lon) {
                  const feature = {
                    type: 'Feature',
                    geometry: {
                      type: 'Point',
                      coordinates: [area.lon, area.lat],
                    },
                    properties: {
                      vehicle_vin: vehicle.vin,
                      vehicle_truck_number: vehicle.truck_number,
                      vehicle_description: vehicle.description,
                      vehicle_id: vehicle.id,
                      area_id: area.id,
                      area_is_type: area.is_type,
                      area_code: area.code,
                      area_name: area.name,
                      area_centroid: area.centroid,
                      area_state_area_id: area.state_area_id,
                      area_is_unverified: area.is_unverified,
                      area_five_zip_city: area.five_zip_city,
                      area_five_zip_state: area.five_zip_state,
                      area_centroid_svg_x: area.centroid_svg_x,
                      area_centroid_svg_y: area.centroid_svg_y,
                      area_located_at: area.located_at,
                      area_lat: area.lat,
                      area_lon: area.lon,
                      area_historical_touches_count:
                        area.historical_touches_count,
                    },
                  };
                  geoJsonFeatures.push(feature);
                }
              });
            }
          });
        }

        const geoJson = {
          type: 'FeatureCollection',
          features: geoJsonFeatures,
        };

        commit('setEldTouchesGeoJson', geoJson);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // THIS IS THE TITLE BAR SEARCHING WHERE A USER ENTERS ONLY A NAME OR AN IDENTIFIER
  // TO FIND A CARRIER
  async searchCarriersByNameOrIdentifier(
    { commit },
    { searchText, page, itemsPerPage }
  ) {
    var params = {
      page: page,
      per_page: itemsPerPage,
      // Only carrier records where legal_name is present to skip
      // over new carrier records coming in from the L&I feed
      'q[legal_name_search_not_null]': 1,
      'q[latest_census_id_or_identifiers_verified_by_not_null]': 1,
      // If they are searching with a number, use against identifiers
      // only show active carriers on global search
    };

    // check if the search text has any letters; if doesn't have letters
    // treat as an identifier search else search based on name
    if (searchText) {
      searchText = searchText?.toUpperCase();
      if (/^[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(searchText);
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^MC[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'MC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^MC-[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'MC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^MC [\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'MC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^DOT[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'DOT';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^DOT-[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'DOT';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^DOT [\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'DOT';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^MX[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'MX';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^MX-[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'MX';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^MX [\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'MX';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^FF[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'FF';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^FF-[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'FF';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^FF [\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'FF';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^SCAC[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'SCAC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^SCAC-[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'SCAC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^SCAC [\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'SCAC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^CVOR[\s0-9-]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = searchText
          .replaceAll('-', '')
          .replace(/^CVOR\s?/, '');
        params['q[identifiers_is_type_eq]'] = 'CVOR';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^(NIR\s?)?[A-Z]-?\d{6}-?\d$/.test(searchText)) {
        const cleanedInput = searchText
          .replace(/^NIR\s?/, '')
          .replace(/^([A-Z])-?/, '$1');
        const cleanedValue = cleanedInput.replace(/-/g, '');
        params['q[identifiers_value_non_padded_start]'] = cleanedValue;
        params['q[identifiers_is_type_eq]'] = 'NIR_QC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^NSC_?([A-Z]{2})?\s?\d+(-\d+)*$/.test(searchText)) {
        const cleanedValue = searchText
          .replace(/^NSC_?[A-Z]{2}?\s?/, '')
          .replace('NSC', '')
          .replace(/^(\d+-?\d*)$/, '$1')
          .trim();
        const provinceCodeMatch = searchText.match(/^NSC_?([A-Z]{2})?\s?/);
        const provinceCode =
          provinceCodeMatch && provinceCodeMatch[1]
            ? provinceCodeMatch[1]
            : null;
        params['q[identifiers_value_non_padded_start]'] = cleanedValue;
        if (provinceCode) {
          params['q[identifiers_is_type_eq]'] =
            'NSC' + (provinceCode ? '_' + provinceCode : '');
        }
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^[0-9-]+$/.test(searchText)) {
        params['q[identifiers_value_eq]'] = searchText;
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^RFC[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'RFC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^RFC-[\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'RFC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^RFC [\s0-9]*$/.test(searchText)) {
        params['q[identifiers_value_non_padded_start]'] = parseInt(
          searchText.replace(/\D/g, '')
        );
        params['q[identifiers_is_type_eq]'] = 'RFC';
        params['q[s]'] = 'legal_name_search asc';
      } else if (/^RFC/.test(searchText)) {
        params['q[identifiers_value_eq]'] = searchText
          .replace(/RFC/g, '')
          .replace(/-/g, '');
        params['q[identifiers_is_type_eq]'] = 'RFC';
        params['q[s]'] = 'legal_name_search asc';
      } else {
        // Search the predicate columns
        params['q[legal_name_search_or_dba_name_search_start]'] = searchText
          ?.replace(/[^a-z0-9]/gi, '')
          ?.toUpperCase();
        params['q[s]'] = 'legal_name_search asc';
      }
    }
    try {
      const res = await axios.get(
        process.env.API_MONITOR + `/api/v1/carriers/global_search`,
        {
          params: params,
        }
      );
      if (res) {
        commit('setCarrierSearchResults', res?.data);
        commit('setPagination', res?.headers);
      }
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // THIS IS NOT THE CORRECT NAMING.  THIS IS ACTUALLY THE RADIUS SEARCH WHERE WE ALLOW THE USER
  // TO FILTER CARRIERS BY A BUNCH OF DIFFERENT CRTIERIA.
  async radiusSearchCarriers(
    { commit },
    { searchText, page, itemsPerPage, filters, signal }
  ) {
    var params = {
      page: page,
      per_page: itemsPerPage,
      // Only carrier records where legal_name is present to skip
      // over new carrier records coming in from the L&I feed
      'q[legal_name_search_not_null]': 1,
      'q[latest_census_id_or_identifiers_verified_by_not_null]': 1,
      // If they are searching with a number, use against identifiers
      // only show active carriers on global search
    };
    if (parseInt(searchText)) {
      params['q[identifiers_value_non_padded_start]'] = parseInt(searchText);
      params['q[s]'] = 'identifiers_value_non_padded asc';
    } else {
      params['q[legal_name_search_or_dba_name_search_cont]'] = searchText;
      params['q[s]'] = 'legal_name_search asc';
    }

    if (filters) {
      for (const key in filters) {
        if (Object.prototype.hasOwnProperty.call(filters, key)) {
          params[`q[${key}]`] = filters[key];
        }
      }
    }
    try {
      const res = await axios.get(
        process.env.API_MONITOR + `/api/v1/carriers`,
        {
          params: params,
          signal,
        }
      );
      if (res) {
        commit('setCarrierSearchResults', res?.data);
        commit('setPagination', res.headers);
      }
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCarriersByCompanyId(
    { commit },
    { companyId, page, itemsPerPage, searchText }
  ) {
    const params = {
      page,
      'q[s]': 'legal_name_search asc',
      per_page: itemsPerPage,
      'q[subscriptions_company_id_eq]': companyId,
    };

    if (parseInt(searchText)) {
      params['q[identifiers_value_non_padded_start]'] = parseInt(searchText);
      params['q[s]'] = 'identifiers_value_non_padded asc';
    } else {
      params['q[legal_name_search_or_dba_name_search_cont]'] = searchText;
    }
    try {
      const res = await axios.get(
        process.env.API_MONITOR + `/api/v1/carriers`,
        {
          params,
        }
      );

      commit('setMycarrierResults', res.data);
      commit('setMyCarrierTotalResultCount', res.headers.totalitems);
      commit('setPagination', res?.headers);
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async createSubscription({ state, commit }, carrierId) {
    try {
      let res = await axios.post(
        process.env.API_MONITOR + '/api/v1/subscriptions/create_by_carrier',
        { carrier_id: carrierId }
      );
      if (state.showCarrier?.id == carrierId) {
        commit('updateShowCarrierSubscription', res.data);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async deleteSubscription({ state, commit }, carrierId) {
    try {
      let res = await axios.delete(
        process.env.API_MONITOR +
          '/api/v1/subscriptions/destroy_by_carrier?carrier_id=' +
          carrierId
      );
      if (state.showCarrier?.id == carrierId) {
        commit('updateShowCarrierSubscription', {});
      }
      commit(
        'setMycarrierResults',
        state.myCarrierResults.filter((carrier) => carrier.id !== carrierId)
      );
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  async loadVehicles(
    { commit, state },
    {
      carrierId,
      perPage,
      page,
      signal,
      vehicleTypes = [],
      trailerTypes = [],
      powerUnitTypes = [],
      vehicleSources = [],
      vehicleTypeFilters = [],
    }
  ) {
    try {
      const queryParams = {
        'q[s]': ['is_type desc', 'trailer_type asc', 'date desc'],
        'q[date_gteq]': dayjs().subtract(2, 'year').format('YYYY-MM-DD'),
        'q[carrier_id_eq]': carrierId,
        per_page: perPage,
        page,
      };

      if (vehicleTypeFilters.length > 0) {
        vehicleTypeFilters.forEach((filter) => {
          if (
            filter.vehicle_type === 'Passenger Car' &&
            filter.power_unit_type === null
          ) {
            queryParams['q[vehicle_vehicle_type_in]'] = 'PASSENGER CAR';
          }
        });
      }

      if (vehicleTypes.length > 0) {
        const powerUnitTypes = ['straight_box_trucks', 'cargo_vans', 'pickup'];
        const isPowerUnitType = vehicleTypes.every((type) =>
          powerUnitTypes.includes(type)
        );

        if (isPowerUnitType) {
          queryParams['q[vehicle_power_unit_type_in]'] = vehicleTypes;
        } else {
          queryParams['q[vehicle_is_type_in]'] = vehicleTypes;
        }
      }

      if (trailerTypes.length > 0) {
        queryParams['q[vehicle_trailer_type_in]'] = trailerTypes;
      }

      if (powerUnitTypes.length > 0) {
        queryParams['q[vehicle_power_unit_type_in]'] = powerUnitTypes;
      }

      if (vehicleSources.length > 0) {
        if (vehicleSources?.includes('no_insurance_certificate')) {
          queryParams['q[source_not_in]'] = ['insurance_certificate'];
        } else {
          queryParams['q[source_in]'] = vehicleSources;
        }
      }

      let res = await axios.get(
        process.env.API_MONITOR + `/api/v1/carriers/${carrierId}/vehicles`,
        {
          params: queryParams,
          signal,
        }
      );

      commit('setShowCarrierEquipment', res);
      console.log('vehicle headers');
      console.dir(res.headers);
      if (
        res?.headers?.scheduledvehiclecount &&
        state.showCarrierScheduledVehicleCount == null
      ) {
        commit(
          'setShowCarrierScheduledVehicleCount',
          res.headers.scheduledvehiclecount
        );
      }
      if (
        res?.headers?.nonscheduledvehiclecount &&
        state.showCarrierNonScheduledVehicleCount == null
      ) {
        commit(
          'setShowCarrierNonScheduledVehicleCount',
          res.headers.nonscheduledvehiclecount
        );
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  async getRulesSyntax({ commit }) {
    try {
      const res = await axios.get(
        process.env.API_MONITOR + '/api/v1/rules/is_types'
      );
      if ([200, 201].includes(res.status)) {
        commit('setRulesSyntax', res.data);
      }
      return res.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  async getRulesSyntaxByClassificationId({ commit }, classification_id) {
    try {
      const res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/rules/is_types?classification_id=' +
          classification_id
      );
      commit('setRulesSyntaxByClassificationId', {
        data: res.data,
        classification_id,
      });
      return res.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getTriumphPayLoadAnalysis({ commit }, id) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${id}/triumph_pay_load_analysis`
      );
      commit('setLoadAnalysis', res.data);
      return res.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getAllRates({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[creator_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        // searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_cont]=${searchTerm}`;
        searchParam = `&q[carrier_legal_name_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations?q[s]=id+desc${userParam}${searchParam}&page=${currentPage}&per_page=50`
      );
      commit('setRates', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getViewedRates({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[creator_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        // searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_cont]=${searchTerm}`;
        searchParam = `&q[carrier_legal_name_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations?q[s]=id+desc${userParam}${searchParam}&q[status_eq]=viewed&page=${currentPage}&per_page=50`
      );
      commit('setViewedRates', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getDeliveredRates({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[creator_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        // searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_cont]=${searchTerm}`;
        searchParam = `&q[carrier_legal_name_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations?q[s]=id+desc${userParam}${searchParam}&q[status_eq]=delivered&page=${currentPage}&per_page=50`
      );
      commit('setDeliveredRates', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getAllRateConEmails({ commit, rootState, state }, currentPage) {
    let userId = null;
    let userParam = '';
    let searchTerm = state.rateConSearchTerm;
    let searchParam = '';
    try {
      if (state.onlyMyRateCons) {
        userId = rootState.users.currentUser?.id;
        userParam = `&q[creator_id_eq]=${userId}`;
      }
      if (searchTerm?.length > 0) {
        // searchParam = `&q[carrier_legal_name_or_carrier_identifiers_value_cont]=${searchTerm}`;
        searchParam = `&q[subject_i_cont]=${searchTerm}`;
      }
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmation_mails?q[s]=id+desc${userParam}${searchParam}&page=${currentPage}&per_page=50`
      );
      commit('setRateConEmails', res);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getRateDetail({ commit }, id) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations/${id}`
      );
      commit('setCurrentRate', res?.data);
      return res?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getRateTerms({ commit }, id) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/rate_confirmations/${id}/get_terms`
      );
      commit('setCurrentRateTerms', res?.data);
      return res?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async showOnlyMyRateCons({ commit }, val) {
    commit('setOnlyMyRateCons', val);
  },
  async saveRateConSearchTerm({ commit }, val) {
    commit('setRateConSearchTerm', val);
  },
  initializeStore({ commit }) {
    if (window.location.href.includes('carriers/global-search')) {
      if (
        localStorage.getItem('carrierSearchResultsQueryUrl') ==
        window.location.href
      ) {
        const localStorageCarrierSearchResults = localStorage.getItem(
          'carrierSearchResults'
        );
        const localStorageCarrierSearchResultsPagination = localStorage.getItem(
          'carrierSearchResultsPagination'
        );
        console.log(
          'localStorageCarrierSearchResultsPagination',
          localStorageCarrierSearchResultsPagination
        );
        if (
          localStorageCarrierSearchResults &&
          localStorageCarrierSearchResults != 'undefined'
        ) {
          commit(
            'setInitialCarrierSearchResults',
            JSON.parse(localStorageCarrierSearchResults)
          );
        }
        if (
          localStorageCarrierSearchResultsPagination &&
          localStorageCarrierSearchResultsPagination != 'undefined'
        ) {
          commit(
            'setPagination',
            JSON.parse(localStorageCarrierSearchResultsPagination)
          );
        }
      } else {
        localStorage.removeItem('carrierSearchResults');
        localStorage.removeItem('carrierSearchResultsPagination');
      }
    }
  },
  async getPowerUnitStats({ commit }, id) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${id}/power_unit_stats`
      );
      commit('setPowerUnitStats', res?.data);
      return res?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
  async getEldStatus({ state }) {
    const carrier_id = state.showCarrier?.id;
    let res;
    if (!carrier_id || carrier_id < 1) {
      res = { status: 404, data: {} };
      return res;
    }
    try {
      res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${carrier_id}/eld_status`
      );
      return res;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        res = e?.response;
        return res;
      }
    }
  },
  async getCarrierAreasGeoJson({ commit }, { id, start_date, end_date }) {
    try {
      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;

      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${id}/vehicle_heatmap`,
        { params }
      );

      const hasData =
        res.data && res.data.features && res.data.features.length > 0;
      commit('setHasHeatmapData', hasData);

      return res.data;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 422, 404].includes(e.response.status)
      ) {
        commit('setHasHeatmapData', false);
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getDashboardConfigs({ commit }, carrierId) {
    try {
      let url = `${process.env.API_MONITOR}/api/v1/metabase/dashboard_configs`;
      if (carrierId) {
        url += `?carrier_id=${carrierId}`;
      }
      const res = await axios.get(url);
      commit('setDashboardConfigs', res.data);
      return res.data;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e?.response;
      } else {
        throw e;
      }
    }
  },
};

// mutations
const mutations = {
  setDashboardConfigs(state, data) {
    state.dashboardConfigs = data;
  },
  setShowCarrier(state, carrier) {
    state.showCarrier = carrier;
  },
  setHasHeatmapData(state, hasData) {
    state.hasHeatmapData = hasData;
  },
  setShowCarrierLoading(state, bool) {
    state.isShowCarrierLoading = bool;
  },
  setInspections(state, data) {
    state.showCarrierInspections = data;
  },
  setViolations(state, data) {
    state.showCarrierViolations = data;
  },
  setSafety(state, data) {
    state.showCarrierSafety = data;
  },
  setCrashes(state, data) {
    state.showCarrierCrashes = data;
  },
  setShowCarrierInsights(state, insights) {
    state.showCarrierInsights = insights;
  },
  setCarrierVehicles(state, vehicles) {
    state.carrierVehicles = vehicles;
  },
  setHasCarrierVehiclesLoaded(state, hasLoaded) {
    state.hasCarrierVehiclesLoaded = hasLoaded;
  },
  setShowCarrierInsurancePolicies(state, policies) {
    state.showCarrierInsurancePolicies = policies;
  },
  setShowCarrierHistoricalTouches(state, historicalTouches) {
    state.showCarrierHistoricalTouches = historicalTouches.areas;
  },
  setShowCarrierELDTouches(state, eldTouches) {
    state.showCarrierELDTouches = eldTouches;
  },
  setShowCarrierEquipment(state, equipment) {
    const headers = equipment?.headers;
    state.showCarrierEquipment.pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.showCarrierEquipment.rows = equipment.data;
  },
  setRulesSyntaxByClassificationId(state, payload) {
    const { data, classification_id } = payload;
    state.rulesSyntaxByClassificationId[classification_id] = {
      ...data,
    };
  },
  setCarrierAgreements(state, carrierAgreements) {
    state.carrierAgreements = carrierAgreements;

    let types = new Set();
    let unduplicated = [];

    let sortedAgreements = carrierAgreements?.sort((a, b) => b.id - a.id);
    // ensure broker_uploaded contracts are at the top of the list within each onboarding packet
    sortedAgreements = sortedAgreements?.sort((a, b) => {
      if (a.onboarding_packet_id == b.onboarding_packet_id) {
        if (a.is_broker_uploaded == b.is_broker_uploaded) {
          return b.id - a.id;
        } else if (a.is_broker_uploaded) {
          return -1;
        } else if (b.is_broker_uploaded) {
          return 1;
        }
      } else {
        return b.id - a.id;
      }
    });

    sortedAgreements.forEach((record) => {
      if (!types.has(record.contract.name)) {
        types.add(record.contract.name);
        unduplicated.push(record);
      }
    });
    state.sortedCarrierAgreements = unduplicated;
  },
  setInitialCarrierSearchResults(state, results) {
    if (
      localStorage.getItem('carrierSearchResultsQueryUrl') ==
      window.location.href
    ) {
      if (!results || results?.length == 0) {
        state.searchResults = [];
      } else {
        state.searchResults = results;
      }
    } else {
      state.searchResults = [];
    }
  },
  setCarrierSearchResults(state, results) {
    // state.searchResults = results;
    localStorage.setItem('carrierSearchResults', JSON.stringify(results));
    if (window.location.href.includes('carriers/global-search')) {
      localStorage.setItem(
        'carrierSearchResultsQueryUrl',
        window.location.href
      );
    }
    if (!results || results?.length == 0) {
      state.searchResults = [];
    } else {
      state.searchResults = results;
    }
  },
  setMycarrierResults(state, results) {
    state.myCarrierResults = results;
  },
  setPagination(state, headers) {
    if (headers) {
      localStorage.setItem(
        'carrierSearchResultsPagination',
        JSON.stringify(headers)
      );
      state.pagination = {
        currentPage: parseInt(headers.currentpage),
        totalPages: parseInt(headers.totalpages),
        totalRecords: parseInt(headers.totalitems),
      };
    } else {
      localStorage.removeItem('carrierSearchResultsPagination');
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
      };
    }
  },
  updateShowCarrierSubscription(state, subscription) {
    state.showCarrier.subscription = subscription;
  },
  setRequestFilters(state, filters) {
    state.filters = filters;
  },
  setRulesSyntax(state, data) {
    state.rulesSyntax = data;
  },
  setMyCarrierTotalResultCount(state, count) {
    state.myCarrierTotalResultCount = count;
  },
  setShowCarrierScheduledVehicleCount(state, count) {
    state.showCarrierScheduledVehicleCount = count;
  },
  setShowCarrierNonScheduledVehicleCount(state, count) {
    state.showCarrierNonScheduledVehicleCount = count;
  },
  setAssociates(state, carriers) {
    state.showAssociatedCarriers.carriers = carriers;
  },
  setAssociatedPagination(state, headers) {
    state.showAssociatedCarriers.pagination = {
      currentPage: parseInt(headers.currentpage) || 0,
      totalPages: parseInt(headers.totalpages) || 0,
      totalRecords: parseInt(headers.totalitems) || 0,
    };
    if (state.showAssociatedCarriers.totalAssociated === 0) {
      state.showAssociatedCarriers.totalAssociated =
        parseInt(headers.totalitems) || 0;
    }
  },
  setAssociatedCounters(state, headers) {
    state.showAssociatedCarriers.counters = {
      totalEmails: parseInt(headers.totalemails) || 0,
      totalPhones: parseInt(headers.totalphones) || 0,
      totalAddresses: parseInt(headers.totaladdresses) || 0,
      totalContacts: parseInt(headers.totalcontacts) || 0,
      totalRemoteAddresses: parseInt(headers.totalremoteaddresses) || 0,
      totalVehicleVins: parseInt(headers.totalvehiclevins) || 0,
    };
  },
  setLoadAnalysis(state, data) {
    state.loadAnalysis = data;
  },
  setRates(state, res) {
    const headers = res?.headers;
    state.rates_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.rates = res?.data;
  },
  setViewedRates(state, res) {
    const headers = res?.headers;
    state.viewed_rates_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.viewed_rates = res?.data;
  },
  setDeliveredRates(state, res) {
    const headers = res?.headers;
    state.delivered_rates_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.delivered_rates = res?.data;
  },
  setRateConEmails(state, res) {
    const headers = res?.headers;
    state.rate_con_emails_pagination = {
      currentPage: Number(headers.currentpage),
      totalPages: Number(headers.totalpages),
      totalRecords: Number(headers.totalitems),
    };
    state.rate_con_emails = res?.data;
  },
  setCurrentRate(state, data) {
    state.current_rate = data;
  },
  setRateConfirmation(state, data) {
    state.rate_confirmation = data;
  },
  setCurrentRateTerms(state, data) {
    state.current_rate_terms = data;
  },
  setOnlyMyRateCons(state, bool) {
    state.onlyMyRateCons = bool;
  },
  setRateConSearchTerm(state, term) {
    state.rateConSearchTerm = term;
  },
  setPowerUnitStats(state, data) {
    state.powerUnitStats = data;
  },
  setAssociatedCarrierIdPersisted(state, carrierId) {
    state.associatedCarrierIdPersisted = carrierId;
  },
  setEldStatus(state, data) {
    state.eld_status = data;
  },
  resetCarrierProfile(state) {
    state.showCarrier = null;
    state.showCarrierSafety = null;
    state.showCarrierCrashes = null;
    state.showCarrierViolations = null;
    state.showCarrierInspections = null;
    state.showCarrierInsights = [];
    state.showCarrierInsurancePolicies = [];
    state.showCarrierHistoricalTouches = [];
    state.showCarrierELDTouches = [];
    state.showAssociatedCarriers = {
      pagination: {
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
      },
      counters: {
        totalEmails: 0,
        totalPhones: 0,
        totalAddresses: 0,
        totalContacts: 0,
        totalRemoteAddresses: 0,
        totalVehicleVins: 0,
      },
      carriers: [],
      totalAssociated: 0,
    };
    state.showCarrierEquipment = {
      pagination: {
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
      },
      rows: [],
    };
    state.showCarrierScheduledVehicleCount = null;
    state.showCarrierNonScheduledVehicleCount = null;
    state.carrierAgreements = [];
    state.sortedCarrierAgreements = [];
    state.eldTouchesGeoJson = null;
  },
  setEldTouchesGeoJson(state, data) {
    state.eldTouchesGeoJson = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
